import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Button, Table, Alert, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import {
  fetchBudgetMeta,
  fetchBudgetPeriods,
  fetchBudgetPeriod,
  createBudgetPeriod,
  ApiError,
} from './api';
import BasicLayout from '../../components/layout/BasicLayout';
import './BudgetArmPage.css';

const { Option } = Select;

const FIELDS = ['plan', 'exec'];
const cellKey = (rowId, field) => `${rowId}-${field}`;
const pkey = (m, y) => `${m}.${y}`;

const nf = (n) =>
  n == null || Number.isNaN(n)
    ? null
    : Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const NUM_RE = /^-?\d{1,14}([.,]\d{1,4})?$/;
function num(raw) {
  const s = String(raw)
    .trim()
    .replace(/[\s\u00a0]/g, '')
    .replace(/\u2212/g, '-');
  if (s === '') return { empty: true };
  if (/^-?\d+([.,]\d+)?$/.test(s) && !NUM_RE.test(s)) return { range: true };
  if (!NUM_RE.test(s)) return { nan: true };
  return { v: parseFloat(s.replace(',', '.')), s };
}
const toSend = (raw) =>
  String(raw)
    .trim()
    .replace(/[\s\u00a0]/g, '')
    .replace(/\u2212/g, '-')
    .replace(',', '.');

const EMPTY_ERRORS = { alert: null, bad: new Set(), warn: new Set(), monthDup: false };

export default function BudgetArmPage() {
  const { t, i18n } = useTranslation();
  const lang = /^(kk|kz)/i.test(i18n.language || '') ? 'kk' : 'ru';

  /* --------------------------------- Мета --------------------------------- */
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(false);

  const nameOf = useCallback((r) => (lang === 'kk' ? r.name_kk : r.name_ru), [lang]);
  const typeOf = useCallback((r) => (lang === 'kk' ? r.type_kk : r.type), [lang]);
  const monthName = useCallback(
    (m) => (meta && meta.months ? meta.months[lang][m - 1] : String(m)),
    [meta, lang]
  );

  const [side, setSide] = useState('income');
  const [year, setYear] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [periods, setPeriods] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [viewKey, setViewKey] = useState(null);
  const [formYear, setFormYear] = useState(null);
  const [formMonth, setFormMonth] = useState('');
  const [cells, setCells] = useState({});
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [saving, setSaving] = useState(false);
  const matrixRef = useRef(null);

  const rows = useMemo(() => (meta ? meta.rows[side] || [] : []), [meta, side]);

  // Форма ввода — все годы из meta.
  const years = useMemo(() => (meta ? meta.years || [] : []), [meta]);
  // Перечень — только годы с введёнными данными (пустые не показываем).
  const listYears = useMemo(() => (meta ? meta.years_with_data || meta.years || [] : []), [meta]);

  const enteredKeys = useMemo(
    () => new Set(periods.filter((p) => p.entered).map((p) => p.key)),
    [periods]
  );
  const periodByKey = useMemo(() => {
    const m = {};
    periods.forEach((p) => {
      m[p.key] = p;
    });
    return m;
  }, [periods]);

  useEffect(() => {
    let alive = true;
    fetchBudgetMeta()
      .then((m) => {
        if (!alive) return;
        setMeta(m);
        const ys = m.years_with_data || m.years || [];
        setYear(ys[0] || new Date().getFullYear());
      })
      .catch(() => alive && setMetaError(true));
    return () => {
      alive = false;
    };
  }, []);

  const loadPeriods = useCallback(() => {
    if (year == null) return;
    setListLoading(true);
    fetchBudgetPeriods(side, year)
      .then((r) => setPeriods(r.periods || []))
      .catch(() => setPeriods([]))
      .finally(() => setListLoading(false));
  }, [side, year]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const listData = useMemo(() => {
    if (year == null) return [];
    const all = [];
    for (let m = 1; m <= 12; m += 1) {
      const key = pkey(m, year);
      const p = periodByKey[key];
      const entered = !!p;
      all.push({
        key,
        month: m,
        entered,
        rows_count: entered ? p.rows_count : null,
        plan: entered ? p.plan : null,
        exec: entered ? p.exec : null,
        pct: entered ? p.pct : null,
      });
    }
    return all.filter((r) => {
      if (statusFilter === 'on') return r.entered;
      if (statusFilter === 'off') return !r.entered;
      return true;
    });
  }, [year, periodByKey, statusFilter]);

  const periodLabel = useCallback(
    (key) => {
      const [m, y] = key.split('.');
      return `${monthName(+m)} ${y} г.`;
    },
    [monthName]
  );

  const columns = [
    {
      title: t('budget.colPeriod'),
      key: 'period',
      render: (_, r) => (
        <div>
          <div>{periodLabel(r.key)}</div>
          <div className="ba-sub">{r.key}</div>
        </div>
      ),
    },
    {
      title: t('budget.colRows'),
      key: 'rows',
      align: 'right',
      render: (_, r) =>
        r.entered ? (
          <span className="ba-num">{r.rows_count}</span>
        ) : (
          <span className="ba-dash">—</span>
        ),
    },
    {
      title: t('budget.colPlan'),
      key: 'plan',
      align: 'right',
      render: (_, r) =>
        r.entered ? (
          <span className="ba-num">{nf(r.plan)}</span>
        ) : (
          <span className="ba-dash">—</span>
        ),
    },
    {
      title: t('budget.colExec'),
      key: 'exec',
      align: 'right',
      render: (_, r) =>
        r.entered ? (
          <span className="ba-num">{nf(r.exec)}</span>
        ) : (
          <span className="ba-dash">—</span>
        ),
    },
    {
      title: t('budget.colPct'),
      key: 'pct',
      align: 'right',
      render: (_, r) =>
        r.entered && r.pct != null ? (
          <span className="ba-num">{r.pct.toFixed(1)}</span>
        ) : (
          <span className="ba-dash">—</span>
        ),
    },
    {
      title: t('budget.colState'),
      key: 'state',
      render: (_, r) =>
        r.entered ? (
          <span className="ba-pill on">{t('budget.stateOn')}</span>
        ) : (
          <span className="ba-pill off">{t('budget.stateOff')}</span>
        ),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (_, r) =>
        r.entered ? (
          <Button size="small" onClick={() => openView(r.key)}>
            {t('budget.actView')}
          </Button>
        ) : (
          <Button size="small" type="primary" onClick={() => openCreate(r.key)}>
            {t('budget.actEnter')}
          </Button>
        ),
    },
  ];

  const derived = useMemo(() => {
    const rowPct = {};
    let totPlan = 0;
    let totExec = 0;
    let filled = 0;
    rows.forEach((r) => {
      const c = cells[r.row_id] || {};
      const p = num(c.plan == null ? '' : c.plan);
      const e = num(c.exec == null ? '' : c.exec);
      if (p.v != null && e.v != null) {
        rowPct[r.row_id] = p.v ? ((e.v / p.v) * 100).toFixed(1) : '—';
        totPlan += p.v;
        totExec += e.v;
        filled += 1;
      } else {
        rowPct[r.row_id] = '—';
      }
    });
    return {
      rowPct,
      totPlan: filled ? nf(totPlan) : '—',
      totExec: filled ? nf(totExec) : '—',
      totPct: filled && totPlan ? ((totExec / totPlan) * 100).toFixed(1) : '—',
      filled,
    };
  }, [rows, cells]);

  function resetForm() {
    setErrors({ alert: null, bad: new Set(), warn: new Set(), monthDup: false });
  }

  function openCreate(prefKey) {
    const listFull = enteredKeys.size >= 12;
    const defYear = prefKey
      ? +prefKey.split('.')[1]
      : listFull
      ? years[years.length - 1] || year
      : year;
    setMode('create');
    setViewKey(null);
    setFormYear(defYear);
    setFormMonth(prefKey ? +prefKey.split('.')[0] : '');
    setCells({});
    resetForm();
    setModalOpen(true);
  }

  function openView(key) {
    setMode('view');
    setViewKey(key);
    setFormYear(+key.split('.')[1]);
    setFormMonth(+key.split('.')[0]);
    resetForm();
    setCells({});
    setModalOpen(true);
    fetchBudgetPeriod(side, key)
      .then((r) => {
        const next = {};
        (r.rows || []).forEach((row) => {
          next[row.row_id] = {
            plan: row.plan == null ? '' : String(row.plan),
            exec: row.exec == null ? '' : String(row.exec),
          };
        });
        setCells(next);
      })
      .catch(() => setCells({}));
  }

  function closeModal() {
    if (saving) return;
    setModalOpen(false);
  }

  const onCell = (rowId, field, value) => {
    setCells((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], [field]: value },
    }));
    setErrors((prev) => {
      const k = cellKey(rowId, field);
      if (!prev.bad.has(k) && !prev.warn.has(k)) return prev;
      const bad = new Set(prev.bad);
      const warn = new Set(prev.warn);
      bad.delete(k);
      warn.delete(k);
      return { ...prev, bad, warn };
    });
  };

  const isDup = useMemo(() => {
    if (!formMonth) return false;
    if (+formYear !== +year) return false; // перечень загружен для year
    return enteredKeys.has(pkey(formMonth, formYear));
  }, [formMonth, formYear, year, enteredKeys]);

  const isBalanceRow = (r) => r.type === 'ИСПОЛЬЗУЕМЫЕ ОСТАТКИ БЮДЖЕТНЫХ СРЕДСТВ';

  function validate() {
    if (!formMonth) {
      setErrors({ alert: null, bad: new Set(), warn: new Set(), monthDup: false, monthReq: true });
      return null;
    }
    if (isDup) {
      setErrors({ alert: null, bad: new Set(), warn: new Set(), monthDup: true });
      return null;
    }
    const bad = new Set();
    const warn = new Set();
    let empty = 0;
    const nan = [];
    const range = [];
    const neg = [];
    const negWarn = [];

    rows.forEach((r) => {
      const c = cells[r.row_id] || {};
      FIELDS.forEach((field) => {
        const res = num(c[field] == null ? '' : c[field]);
        const k = cellKey(r.row_id, field);
        if (res.empty) {
          empty += 1;
          bad.add(k);
          return;
        }
        if (res.range) {
          range.push(r.sort_order);
          bad.add(k);
          return;
        }
        if (res.nan) {
          nan.push(r.sort_order);
          bad.add(k);
          return;
        }
        if (res.v < 0) {
          if (side === 'expense') {
            neg.push(r.sort_order);
            bad.add(k);
          } else if (!isBalanceRow(r)) {
            negWarn.push(r.sort_order);
            warn.add(k);
          }
        }
      });
    });

    const lst = (a) => [...new Set(a)].sort((x, y) => x - y).join(', ');

    if (empty) {
      setErrors({
        alert: { kind: 'bad', text: t('budget.errEmpty', { n: empty }) },
        bad,
        warn,
        monthDup: false,
      });
      return null;
    }
    if (nan.length) {
      setErrors({
        alert: { kind: 'bad', text: t('budget.errNum', { n: lst(nan) }) },
        bad,
        warn,
        monthDup: false,
      });
      return null;
    }
    if (range.length) {
      setErrors({
        alert: { kind: 'bad', text: t('budget.errRange', { n: lst(range) }) },
        bad,
        warn,
        monthDup: false,
      });
      return null;
    }
    if (neg.length) {
      setErrors({
        alert: { kind: 'bad', text: t('budget.errNeg', { n: lst(neg) }) },
        bad,
        warn,
        monthDup: false,
      });
      return null;
    }
    setErrors({
      alert: negWarn.length
        ? { kind: 'warn', text: t('budget.warnNeg', { n: lst(negWarn) }) }
        : null,
      bad,
      warn,
      monthDup: false,
    });

    return {
      side,
      year: Number(formYear),
      month: Number(formMonth),
      rows: rows.map((r) => {
        const c = cells[r.row_id] || {};
        return { row_id: r.row_id, plan: toSend(c.plan), exec: toSend(c.exec) };
      }),
    };
  }

  function scrollToFirstBad() {
    setTimeout(() => {
      const el = matrixRef.current && matrixRef.current.querySelector('.ba-cell.bad');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  }

  const save = async () => {
    if (saving || isDup) return;
    const payload = validate();
    if (!payload) {
      scrollToFirstBad();
      return;
    }
    setSaving(true);
    try {
      const created = await createBudgetPeriod(payload);
      const key = created.key || pkey(payload.month, payload.year);
      setModalOpen(false);
      // Обновляем список лет с данными (в реальном API — свежий /meta),
      // затем перечитываем перечень: если год совпал — тот же, иначе переключаем.
      fetchBudgetMeta()
        .then((m) => setMeta(m))
        .catch(() => {});
      if (+payload.year === +year) loadPeriods();
      else setYear(+payload.year);
      notification.success({
        message: t(side === 'income' ? 'budget.toastOkIncome' : 'budget.toastOkExpense', {
          p: periodLabel(key),
        }),
        description: t('budget.toastSub'),
        placement: 'bottomRight',
      });
    } catch (err) {
      handleSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveError = (err) => {
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 409) {
      setErrors({ alert: null, bad: new Set(), warn: new Set(), monthDup: true }); // АС3
      return;
    }
    if (status === 422 && err.body && Array.isArray(err.body.details)) {
      const bad = new Set();
      err.body.details.forEach((d) => {
        const field = d.field === 'exec' ? 'exec' : 'plan';
        if (d.row_id != null) bad.add(cellKey(d.row_id, field));
      });
      setErrors({
        alert: { kind: 'bad', text: t('budget.saveError') },
        bad,
        warn: new Set(),
        monthDup: false,
      });
      scrollToFirstBad();
      return;
    }
    notification.error({ message: t('budget.saveError'), placement: 'bottomRight' });
  };

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, saving]);

  if (metaError) {
    return (
      <BasicLayout>
        <div className="ba-page">
          <Alert
            type="warning"
            showIcon
            message={t('budget.loadError')}
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                {t('budget.retry')}
              </Button>
            }
          />
        </div>
      </BasicLayout>
    );
  }

  const modalTitle =
    mode === 'view'
      ? `${t(side === 'income' ? 'budget.vTitleIncome' : 'budget.vTitleExpense')} · ${
          viewKey ? periodLabel(viewKey) : ''
        }`
      : t(side === 'income' ? 'budget.mTitleIncome' : 'budget.mTitleExpense');

  let lastType = null;

  return (
    <BasicLayout>
      <div className="ba-page">
        <div className="ba-head">
          <h1 className="ba-title">{t('budget.title')}</h1>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(null)}>
            {t('budget.create')}
          </Button>
        </div>

        <div className="ba-tabs">
          <div
            className={`ba-tab${side === 'income' ? ' on' : ''}`}
            onClick={() => setSide('income')}
          >
            {t('budget.tabIncome')}
          </div>
          <div
            className={`ba-tab${side === 'expense' ? ' on' : ''}`}
            onClick={() => setSide('expense')}
          >
            {t('budget.tabExpense')}
          </div>
        </div>

        <div className="ba-toolbar">
          <label className="ba-fld">
            <span>{t('budget.fYear')}</span>
            <Select value={year} onChange={setYear} style={{ width: 140 }}>
              {listYears.map((y) => (
                <Option key={y} value={y}>
                  {y}
                </Option>
              ))}
            </Select>
          </label>
          <label className="ba-fld">
            <span>{t('budget.fStatus')}</span>
            <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 180 }}>
              <Option value="all">{t('budget.stAll')}</Option>
              <Option value="on">{t('budget.stOn')}</Option>
              <Option value="off">{t('budget.stOff')}</Option>
            </Select>
          </label>
        </div>

        <div className="ba-card">
          <Table
            rowKey="key"
            columns={columns}
            dataSource={listData}
            loading={listLoading}
            locale={{ emptyText: t('budget.emptyList') }}
            pagination={{ pageSize: 12, hideOnSinglePage: true }}
          />
        </div>
      </div>

      {modalOpen && (
        <div
          className="ba-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="ba-modal" role="dialog" aria-modal="true">
            <div className="ba-modal-head">
              <h3>{modalTitle}</h3>
              <button className="ba-modal-x" onClick={closeModal} aria-label="close">
                ✕
              </button>
            </div>

            <div className="ba-modal-body">
              <div className="ba-head-row">
                <div className="ba-field">
                  <label>
                    {t('budget.fYear2')}
                    <span className="ba-req">*</span>
                  </label>
                  <Select
                    value={formYear}
                    onChange={setFormYear}
                    disabled={mode === 'view'}
                    style={{ width: '100%' }}
                  >
                    {years.map((y) => (
                      <Option key={y} value={y}>
                        {y}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div
                  className={`ba-field${
                    errors.monthReq || errors.monthDup ? ' ba-field--err' : ''
                  }`}
                >
                  <label>
                    {t('budget.fMonth')}
                    <span className="ba-req">*</span>
                  </label>
                  <Select
                    value={formMonth || undefined}
                    onChange={(v) => {
                      setFormMonth(v);
                      setErrors((p) => ({ ...p, monthReq: false, monthDup: false }));
                    }}
                    disabled={mode === 'view'}
                    placeholder={t('budget.selMonth')}
                    style={{ width: '100%' }}
                  >
                    {(meta ? meta.months[lang] : []).map((n, i) => (
                      <Option key={i + 1} value={i + 1}>
                        {n}
                      </Option>
                    ))}
                  </Select>
                  {errors.monthDup && <div className="ba-msg">{t('budget.dupMsg')}</div>}
                  {errors.monthReq && !errors.monthDup && (
                    <div className="ba-msg">{t('budget.reqMsg')}</div>
                  )}
                </div>

                <div className="ba-field">
                  <label>{t('budget.fSection')}</label>
                  <div className="ba-side-ro">
                    {t(side === 'income' ? 'budget.tabIncome' : 'budget.tabExpense')}
                  </div>
                </div>
              </div>

              {errors.alert && (
                <div className={`ba-alert ${errors.alert.kind}`}>
                  <span>{errors.alert.kind === 'bad' ? '✕' : '⚠'}</span>
                  <span>{errors.alert.text}</span>
                </div>
              )}

              <div className="ba-matrix" ref={matrixRef}>
                <table>
                  <thead>
                    <tr>
                      <th className="ba-cno">{t('budget.mNo')}</th>
                      <th className="ba-cw">{t('budget.mCode')}</th>
                      <th>{t('budget.mName')}</th>
                      <th className="num">
                        {t('budget.mPlan')}
                        {mode === 'create' && <span className="ba-req">*</span>}
                      </th>
                      <th className="num">
                        {t('budget.mExec')}
                        {mode === 'create' && <span className="ba-req">*</span>}
                      </th>
                      <th className="num">{t('budget.mPct')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const grp = r.type !== lastType ? ((lastType = r.type), typeOf(r)) : null;
                      const c = cells[r.row_id] || {};
                      const badP = errors.bad.has(cellKey(r.row_id, 'plan'));
                      const badE = errors.bad.has(cellKey(r.row_id, 'exec'));
                      const warnP = errors.warn.has(cellKey(r.row_id, 'plan'));
                      const warnE = errors.warn.has(cellKey(r.row_id, 'exec'));
                      // подсветка обязательных пустых клеток (только в режиме ввода)
                      const needP =
                        mode === 'create' && (c.plan == null || String(c.plan).trim() === '');
                      const needE =
                        mode === 'create' && (c.exec == null || String(c.exec).trim() === '');
                      return (
                        <React.Fragment key={r.row_id}>
                          {grp != null && (
                            <tr className="ba-grp-row">
                              <td colSpan={6}>{grp}</td>
                            </tr>
                          )}
                          <tr>
                            <td className="ba-code">{r.sort_order}</td>
                            <td className="ba-code">
                              {r.code ? r.code : <span className="ba-dash">—</span>}
                            </td>
                            <td className="ba-cname">{nameOf(r)}</td>
                            <td className="num">
                              <input
                                className={`ba-cell${badP ? ' bad' : ''}${warnP ? ' warn' : ''}${
                                  needP ? ' need' : ''
                                }`}
                                value={c.plan == null ? '' : c.plan}
                                onChange={(e) => onCell(r.row_id, 'plan', e.target.value)}
                                disabled={mode === 'view'}
                                inputMode="decimal"
                              />
                            </td>
                            <td className="num">
                              <input
                                className={`ba-cell${badE ? ' bad' : ''}${warnE ? ' warn' : ''}${
                                  needE ? ' need' : ''
                                }`}
                                value={c.exec == null ? '' : c.exec}
                                onChange={(e) => onCell(r.row_id, 'exec', e.target.value)}
                                disabled={mode === 'view'}
                                inputMode="decimal"
                              />
                            </td>
                            <td className="ba-pct">{derived.rowPct[r.row_id] || '—'}</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                    <tr className="ba-tot-row">
                      <td />
                      <td />
                      <td>{t('budget.mTotal')}</td>
                      <td className="num">{derived.totPlan}</td>
                      <td className="num">{derived.totExec}</td>
                      <td className="ba-pct">{derived.totPct}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="ba-modal-foot">
              <div className="ba-progress">
                {mode === 'view' ? '' : t('budget.progress', { a: derived.filled, b: rows.length })}
              </div>
              <div className="ba-foot-right">
                <Button onClick={closeModal} disabled={saving}>
                  {mode === 'view' ? t('budget.close') : t('budget.cancel')}
                </Button>
                {mode === 'create' && (
                  <Button type="primary" onClick={save} loading={saving} disabled={isDup}>
                    {t('budget.save')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </BasicLayout>
  );
}
